/**
 * The TLS half of the Cursor-on-Target panel, shown only for a `tls:` endpoint.
 *
 * A TAK server that rejects a client certificate closes the socket and says
 * nothing else, so from this side an expired cert, a cert from the wrong CA and
 * a server that is simply down all look like "not connected". The cert's own
 * subject and expiry are what separate them, which is why they are here rather
 * than only in the server log.
 */

import type { CotTlsStatus } from '@/api/cot';

/** TAK client certs are short-lived. Two weeks is enough time to get a new one issued. */
const EXPIRY_WARNING_DAYS = 14;
const DAY_S = 86_400;

function Cell({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className="well flex min-w-0 flex-col gap-1 rounded-[11px] px-3 py-2">
      <span className="label-mono text-[8px] text-[color:var(--muted-foreground-2)]">{label}</span>
      <span
        className="tnum truncate text-[12.5px]"
        style={tone ? { color: tone } : undefined}
        title={value}
      >
        {value}
      </span>
    </div>
  );
}

export function expiryText(notAfter: number | null, nowS: number): { text: string; tone?: string } {
  if (notAfter == null) return { text: 'no client cert', tone: 'var(--caution)' };
  const date = new Date(notAfter * 1000).toISOString().slice(0, 10);
  const days = Math.floor((notAfter - nowS) / DAY_S);
  if (notAfter <= nowS) return { text: `expired ${date}`, tone: 'var(--danger)' };
  if (days < EXPIRY_WARNING_DAYS) {
    return { text: `${date} (${days} d left)`, tone: 'var(--caution)' };
  }
  return { text: date };
}

export function CotTlsStatusBlock({ tls, nowS }: { tls: CotTlsStatus; nowS: number }) {
  const expiry = expiryText(tls.clientNotAfter, nowS);
  return (
    <div className="flex flex-col gap-2" data-testid="cot-tls">
      <div className="flex items-center gap-2">
        <span className="label-mono text-[9px]">MUTUAL TLS</span>
        <span
          className="ml-auto label-mono text-[8px]"
          style={{ color: tls.connected ? 'var(--ok)' : 'var(--caution)' }}
        >
          {tls.connected ? `CONNECTED ${tls.protocol ?? ''}`.trim() : 'NOT CONNECTED'}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Cell label="CLIENT CERT" value={tls.clientSubject ?? 'none loaded'} />
        <Cell label="CERT EXPIRES" value={expiry.text} tone={expiry.tone} />
        <Cell label="SERVER" value={tls.serverSubject ?? 'not seen yet'} />
        <Cell label="TRUST" value={tls.trust === 'system' ? 'system store' : tls.trust} />
        {/* Shown in the caution colour because it is a deliberate relaxation:
            the chain is still checked, the name on the certificate is not. */}
        <Cell
          label="HOSTNAME CHECK"
          value={tls.verifyHostname ? 'on' : 'off (chain still verified)'}
          tone={tls.verifyHostname ? undefined : 'var(--caution)'}
        />
      </div>
      {tls.lastError ? (
        <p className="text-[10.5px]" style={{ color: 'var(--caution)' }}>
          {tls.lastError}
        </p>
      ) : null}
    </div>
  );
}
